import { useEffect, useState } from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Textarea,
} from '@shared/ui';
import { ApiError } from '@shared/api';
import {
  useUpdateLeadNotesMutation,
  useUpdateLeadTitleMutation,
  type Lead,
} from '@entities/crm';

interface LeadPanelProps {
  lead: Lead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LeadPanel({ lead, open, onOpenChange }: LeadPanelProps) {
  const { t } = useTranslation();
  const titleMutation = useUpdateLeadTitleMutation();
  const notesMutation = useUpdateLeadNotesMutation();

  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (lead) {
      setTitle(lead.title);
      setNotes(lead.notes ?? '');
      setError(null);
    }
  }, [lead]);

  if (!lead) return null;

  const titleDirty = title !== lead.title;
  const notesDirty = (notes || null) !== (lead.notes ?? null);

  const save = () => {
    setError(null);
    const tasks: Promise<unknown>[] = [];
    if (titleDirty) {
      tasks.push(
        titleMutation
          .mutateAsync({ id: lead.id, title: title.trim() })
          .catch((err) => {
            throw err instanceof ApiError ? err : new Error('Title update failed');
          }),
      );
    }
    if (notesDirty) {
      tasks.push(
        notesMutation
          .mutateAsync({ id: lead.id, notes: notes.trim() || null })
          .catch((err) => {
            throw err instanceof ApiError ? err : new Error('Notes update failed');
          }),
      );
    }
    void Promise.all(tasks)
      .then(() => onOpenChange(false))
      .catch((err: Error) => setError(err.message));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('crm.panel.title')}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{t(`crm.columns.${lead.status}`)}</Badge>
            <Badge tone="info">{t(`crm.source.${lead.source}`)}</Badge>
            <span>{t('crm.card.updated', { date: new Date(lead.updatedAt).toLocaleString() })}</span>
          </DialogDescription>
        </DialogHeader>

        <FormField label={t('crm.panel.leadTitle')}>
          {(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </FormField>

        <FormField label={t('crm.panel.notes')} hint={t('crm.panel.notesHint')}>
          {(p) => (
            <Textarea
              {...p}
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </FormField>

        {lead.buyerId ? (
          <p className="text-xs opacity-70">
            {t('crm.panel.buyerId')} <span className="font-mono">{lead.buyerId}</span>
          </p>
        ) : null}

        {error ? <Alert type="error" showIcon message={error} /> : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
          <Button
            type="button"
            disabled={!titleDirty && !notesDirty}
            loading={titleMutation.isPending || notesMutation.isPending}
            onClick={save}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
